import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';

export const CHART_COLORS = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#475569'];

const compact = (v) => {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed(0);
};

const money = (v) => (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const monthLabel = (m) => {
  if (!m || m.length < 7) return m || '—';
  const d = new Date(`${m}-01T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

export function ChartCard({ title, subtitle, children, testid, className = '' }) {
  return (
    <div className={`card-tactile rounded-lg p-4 ${className}`} data-testid={testid}>
      <div className="mb-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

const Empty = () => (
  <div className="h-[220px] flex items-center justify-center text-xs text-muted-foreground">No data for this period</div>
);

// series: [{ key, label, color }]
export function TrendBarChart({ data, series, height = 240, xKey = 'month', isMonth = true }) {
  if (!data || data.length === 0) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey={xKey} tickFormatter={isMonth ? monthLabel : undefined} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
        <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} width={48} />
        <Tooltip
          formatter={(v, n) => [money(v), n]}
          labelFormatter={isMonth ? monthLabel : undefined}
          contentStyle={{ fontSize: 12, borderRadius: 8 }}
        />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {series.map((s) => (
          <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={36} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// data: [{ name, value }]
export function DonutChart({ data, height = 240, onSliceClick }) {
  const rows = (data || []).filter((d) => Math.abs(Number(d.value) || 0) > 0.009).map((d) => ({ ...d, value: Math.abs(Number(d.value)) }));
  if (rows.length === 0) return <Empty />;
  const total = rows.reduce((s, r) => s + r.value, 0);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-3">
      <div className="w-full sm:w-1/2" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2}
              onClick={onSliceClick ? (d) => onSliceClick(d?.payload || d) : undefined}
              className={onSliceClick ? 'cursor-pointer' : ''}>
              {rows.map((r, i) => <Cell key={r.name} fill={r.color || CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [money(v), n]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="w-full sm:w-1/2 space-y-1.5 text-xs max-h-[220px] overflow-y-auto">
        {rows.slice(0, 10).map((r, i) => (
          <li key={r.name} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: r.color || CHART_COLORS[i % CHART_COLORS.length] }} />
            <span className="truncate flex-1" title={r.name}>{r.name}</span>
            <span className="font-mono text-muted-foreground">{total ? ((r.value / total) * 100).toFixed(1) : 0}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
